import { CruisesContract, definitions } from '@luxuryescapes/contract-svc-cruise';

import { SearchFilters } from '~/components/Cruises/pages/Home/components/Sailings/constants';

import cruisesAPI from './CruisesApiService';

type GetRateCodesQueryParams = definitions['GET_RATE_CODES_QUERY'] & {
  rateCodeDescription?: string;
};
export type GetRateCodes = definitions['RATE_CODE_WITH_VENDOR'] & {
  remarks?: string | null;
};
type GetRateCodeById = definitions['GET_RATE_CODE_BY_ID'];
type UpdateRateCodeById = definitions['UPDATE_RATE_CODE_BY_ID'];

export type GetRateCodesResponse = CruisesContract.Response<Array<GetRateCodes>>;
export type GetRateCodeByIdResponse = CruisesContract.Response<GetRateCodeById>;
export type UpdateRateCodeByIdResponse = CruisesContract.Response<UpdateRateCodeById>;

const RESOURCE = 'rate-codes';

const getRateCodeById = (id: string): Promise<GetRateCodeByIdResponse> => {
  return cruisesAPI.http.get({
    resource: `${RESOURCE}/${id}`,
  });
};

const getRateCodeList = async (params?: GetRateCodesQueryParams): Promise<GetRateCodesResponse> => {
  const queryParams = {
    ...(params?.skip && { skip: params.skip }),
    ...(params?.take && { take: params.take }),
    ...(params?.rateCodes && { rateCodes: params.rateCodes }),
    ...(params?.rateCodeDescription && { rateCodeDescription: params.rateCodeDescription }),
    ...(params?.vendorCode && { vendorCode: params.vendorCode }),
    ...(params?.isEnabled !== undefined && { isEnabled: params.isEnabled }),
    ...(params?.isNetRate !== undefined && { isNetRate: params.isNetRate }),
    ...(params?.isFlash !== undefined && { isFlash: params.isFlash }),
    ...(params?.isInRateGroup !== undefined && { isInRateGroup: params.isInRateGroup }),
    ...(params?.rateGroupIds && { rateGroupIds: params.rateGroupIds }),
  };

  return await cruisesAPI.http.get({
    resource: `${RESOURCE}`,
    paginationParams: queryParams,
  });
};

const createBulkRateCodeByDepartureId = async (
  params: SearchFilters & {
    rateCodes: Array<string>;
    departureIds: Array<string>;
    isFlash: boolean;
    isSelectAll: boolean;
  },
): Promise<CruisesContract.Response<CruisesContract.GenericMessageResponse>> => {
  const {
    rateCodes,
    departureIds,
    isFlash,
    isSelectAll,
    name,
    offerId,
    vendorId,
    sailingExternalId,
    sailingPlan,
    sailingDurationMin,
    sailingDurationMax,
    departureDateStart,
    departureDateEnd,
    departurePortExternalIds,
    status: departureStatus,
    hasCoreChanges,
    hasItineraryChanges,
    shipName,
    shipExternalId,
  } = params;

  return await cruisesAPI.http.post({
    resource: `${RESOURCE}`,
    body: {
      rateCodes,
      departureIds,
      isFlash,
      isSelectAll,
      name,
      offerId,
      vendorId,
      sailingExternalId,
      sailingPlan,
      sailingDurationMin,
      sailingDurationMax,
      departureDateStart,
      departureDateEnd,
      departurePortExternalIds,
      departureStatus,
      hasCoreChanges: hasCoreChanges === 'ALL' ? undefined : hasCoreChanges === 'TRUE',
      hasItineraryChanges: hasItineraryChanges === 'ALL' ? undefined : hasItineraryChanges === 'TRUE',
      shipName,
      shipExternalId,
    },
  });
};

const updateRateCodeById = (
  id: string,
  params: {
    isFlash: boolean;
    isEnabled: boolean;
    disabledInBrands: Array<definitions['LE_BRAND']>;
  },
): Promise<CruisesContract.Response<UpdateRateCodeByIdResponse>> => {
  const { isFlash, isEnabled, disabledInBrands } = params;

  return cruisesAPI.http.put({
    resource: `${RESOURCE}/${id}`,
    body: { isFlash, isEnabled, disabledInBrands },
  });
};

export default {
  getRateCodeById,
  getRateCodeList,
  updateRateCodeById,
  createBulkRateCodeByDepartureId,
};
